import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type TokenSource } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * A client's accounts and the money through them (money phase 4: slice 1's api/src/routes/ledger.ts, slice 3's
 * routes/ledger-import.ts). Money is the database's TEXT end to end (web/src/money.ts formats it). An account is only
 * ever its last four here; the full number travels once, in the reveal's answer, and is held in a component's own
 * state, never in the query cache.
 */

export interface LedgerAccount {
  id: string; institution_name: string; kind: string | null; last4: string | null; role: string | null;
  opened_on: string | null; closed_on: string | null; closed_reason: string | null; withdrawn: boolean;
  current_version: number; statement_as_of: string | null; statement_balance: string | null;
  ledger_balance_then: string | null; difference: string | null; balance_today: string | null; number_on_file: boolean;
}
export interface LedgerCategory {
  code: string; label: string; direction: 'in' | 'out' | 'either'; schedule: string | null; schedule_line: string | null;
  links_order: boolean; settles_advances: boolean; active: boolean;
}
export interface LedgerRights {
  record: boolean; correct: boolean; reveal: boolean; see_reveals: boolean; manage_categories: boolean;
  /** may_import_ledger($1), slice 3 (58cf257): AP/AR and tier 1. The Import button is drawn from it. */
  import: boolean;
}
export interface ClientLedger { accounts: LedgerAccount[]; categories: LedgerCategory[]; may: LedgerRights }

export type EntryMethod = 'ach' | 'online' | 'check' | 'cash' | 'transfer' | 'other';
export interface LedgerEntry {
  id: string; account_id: string; posted_on: string; amount: string; category_code: string; category_label: string;
  counterparty: string | null; memo: string | null; method: EntryMethod | null; check_number: string | null;
  receipt_upload_id: string | null; receipt_clio_copy_id: string | null; court_order_id: string | null;
  transfer_group_id: string | null; source: string; withdrawn: boolean; withdrawal_reason: string | null;
  current_version: number;
}
export interface EntryPage { entries: LedgerEntry[]; total: number; net: string; limit: number; offset: number }
export interface EntryFilters { account?: string; from?: string; to?: string; category?: string; limit: number; offset: number }
export interface EntryVersion {
  version: number; fields: Record<string, unknown>; withdrawn: boolean; withdrawal_reason: string | null;
  edit_reason: string | null; reason_text: string | null; by: string | null; recorded_at: string;
}
export interface EntryFields {
  accountId: string; postedOn: string; amount: string; category: string; counterparty?: string | null;
  memo?: string | null; method?: EntryMethod | null; checkNumber?: string | null;
}
export interface ReconciliationRow {
  statement_id: string; as_of: string; statement_balance: string; ledger_balance: string; difference: string;
  entries_since_previous: number;
}

/** The query-key root every ledger read shares, so a write refreshes all of them. Never holds a full number. */
const KEY = (clientId: string) => ['client', clientId, 'ledger'] as const;

export function useClientLedger(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...KEY(clientId), 'accounts'],
    queryFn: () => apiFetch<ClientLedger>(`/clients/${clientId}/ledger`, getToken),
    enabled, ...queryDefaults,
  });
}

/** Fixed-shape keys only in the address (ids, dates, a code, numbers): no free text, ever. */
export function useLedgerEntries(clientId: string, f: EntryFilters, enabled = true) {
  const { getToken } = useSession();
  const qs = new URLSearchParams();
  if (f.account) qs.set('account', f.account);
  if (f.from) qs.set('from', f.from);
  if (f.to) qs.set('to', f.to);
  if (f.category) qs.set('category', f.category);
  qs.set('limit', String(f.limit));
  qs.set('offset', String(f.offset));
  return useQuery({
    queryKey: [...KEY(clientId), 'entries', f],
    queryFn: () => apiFetch<EntryPage>(`/clients/${clientId}/ledger/entries?${qs.toString()}`, getToken),
    enabled, ...queryDefaults, placeholderData: (p) => p,
  });
}

export function useEntryVersions(clientId: string, entryId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...KEY(clientId), 'versions', entryId],
    queryFn: () => apiFetch<{ versions: EntryVersion[] }>(`/ledger/entries/${entryId}/versions`, getToken),
    enabled, ...queryDefaults,
  });
}

export function useReconciliation(clientId: string, accountId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...KEY(clientId), 'reconciliation', accountId],
    queryFn: () => apiFetch<{ statements: ReconciliationRow[] }>(`/accounts/${accountId}/reconciliation`, getToken),
    enabled, ...queryDefaults,
  });
}

export function useAccountReveals(clientId: string, accountId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...KEY(clientId), 'reveals', accountId],
    queryFn: () => apiFetch<{ reveals: { at: string; by_name: string }[] }>(`/accounts/${accountId}/reveals`, getToken),
    enabled, ...queryDefaults,
  });
}

const post = (body: unknown): RequestInit =>
  ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

function useLedgerWrite<V extends { clientId: string }, R>(send: (v: V, token: TokenSource) => Promise<R>) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => send(v, getToken),
    onSuccess: (_r, v) => { void queryClient.invalidateQueries({ queryKey: KEY(v.clientId) }); },
  });
}

export function useRecordEntry() {
  return useLedgerWrite<{ clientId: string; fields: EntryFields }, { entry: LedgerEntry }>((v, t) =>
    apiFetch(`/clients/${v.clientId}/ledger/entries`, t, post(v.fields)));
}
export function useCorrectEntry() {
  return useLedgerWrite<{ clientId: string; entryId: string; fields: EntryFields; reason: string; reasonText?: string },
    { version: number }>((v, t) =>
    apiFetch(`/ledger/entries/${v.entryId}/correct`, t,
      post({ fields: v.fields, reason: v.reason, ...(v.reasonText ? { reasonText: v.reasonText } : {}) })));
}
export function useWithdrawEntry() {
  return useLedgerWrite<{ clientId: string; entryId: string; reason: string }, { version: number }>((v, t) =>
    apiFetch(`/ledger/entries/${v.entryId}/withdraw`, t, post({ reason: v.reason })));
}
export function useRecordStatement() {
  return useLedgerWrite<{ clientId: string; accountId: string; asOf: string; balance: string; source: 'statement' | 'bank letter' },
    { id: string }>((v, t) =>
    apiFetch(`/accounts/${v.accountId}/statements`, t, post({ asOf: v.asOf, balance: v.balance, source: v.source })));
}
export function useWithdrawStatement() {
  return useLedgerWrite<{ clientId: string; statementId: string; reason: string }, { version: number }>((v, t) =>
    apiFetch(`/statements/${v.statementId}/withdraw`, t, post({ reason: v.reason })));
}
/** The full number goes out once, in this body; the answer holds the last four only. */
export function useAddFullNumber() {
  return useLedgerWrite<{ clientId: string; accountId: string; number: string }, { recorded: boolean; last4: string }>((v, t) =>
    apiFetch(`/accounts/${v.accountId}/number`, t, post({ number: v.number })));
}

/**
 * The reveal, NOT a query and NOT a mutation kept in the cache: a plain call whose answer the caller holds in its own
 * state for 30 seconds (A5). The server has written the audit row and committed before it answers.
 */
export async function revealAccountNumber(accountId: string, getToken: TokenSource): Promise<string> {
  const r = await apiFetch<{ number: string }>(`/accounts/${accountId}/reveal`, getToken, { method: 'POST' });
  return r.number;
}

// ---------------------------------------------------------------- the import (slice 3)
export type LineState = 'new' | 'already imported' | 'conflict' | 'refused' | 'held';
export interface PreviewLine {
  ordinal: number; state: LineState; words: string[]; date: string | null; amount: string | null; payee: string | null;
  memo: string | null; category_code: string | null; transfer: string | null; duplicate_of_other_source: boolean;
  /** Quicken's own words for a category Procura does not know: shown here, to the importer, never kept (Q8). */
  unmapped: { hash: string; version: string; text: string }[];
  /** The line's key on a line matching another source's entry, to mark it "not a duplicate" (slice 3, 44c533e); null otherwise. */
  key?: string | null;
}
export interface ImportSection { place: number; account: string; transactions: number; from: string | null; to: string | null }
export interface ImportCounts {
  lines: number; new: number; unchanged: number; conflicts: number; refused: number; held: number; masked_memos: number;
  transfers_paired: number; transfers_awaiting: number; duplicates: number; unmapped: number;
}
export type ImportBlocking = { conflicts: number; refused: number; held: number; unmapped: number; duplicates: number };
export type ImportPreview =
  | { sha256: string; kind: string; sections: ImportSection[]; needsSection: true; changed: boolean }
  | { sha256: string; kind: string; sections: ImportSection[]; needsSection?: false; section: number; changed: boolean;
      lines: PreviewLine[]; counts: ImportCounts; blocking: ImportBlocking; rekeys: number; keys: string[] };
export interface ImportBody { accountId: string; section?: number | null; olderQuicken?: boolean; file: string }

/** Preview and commit are plain calls: the answer lives in the import page's own state, never the query cache (Q8). */
export function previewImport(clientId: string, body: ImportBody, getToken: TokenSource) {
  return apiFetch<ImportPreview>(`/clients/${clientId}/ledger/import/preview`, getToken, post(body));
}
export function commitImport(clientId: string, body: ImportBody & { sha256: string; notDuplicates?: string[] }, getToken: TokenSource) {
  return apiFetch<{ import: string; counts: ImportCounts }>(`/clients/${clientId}/ledger/import/commit`, getToken, post(body));
}
export function mapImportCategory(m: { hash: string; version: string; category: string }, getToken: TokenSource) {
  return apiFetch<{ mapping: string }>('/ledger/import/map', getToken, post(m));
}

// ---------------------------------------------------------------- money owed to Allied (slice 2, not yet built)
/**
 * Slice 2's advances for a client (plan R4, D-125 15), as Code 1 gave the route (provisional until slice 2 commits):
 * GET /clients/:id/advances, the Finance section, a hidden client 403. `owed` is the sum still outstanding
 * (v_outstanding_advances); `open` each standing advance with something still owed, oldest first. Money is text. Only
 * `owed` and `open` are read here. While the route is absent (404) this says nothing: no line is drawn.
 */
export interface OpenAdvances {
  owed: string;
  open: { id: string; advanced_on: string; reason: string; amount: string; outstanding: string }[];
}
export function useOpenAdvances(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...KEY(clientId), 'advances-open'],
    queryFn: async () => {
      try {
        const r = await apiFetch<OpenAdvances>(`/clients/${clientId}/advances`, getToken);
        return { owed: r.owed, open: r.open };
      } catch (err) {
        if ((err as { status?: number }).status === 404) return null;
        throw err;
      }
    },
    enabled, ...queryDefaults, retry: false,
  });
}
